import { Injectable, OnDestroy } from '@angular/core';
import { Firestore, collection, collectionData, query, where } from '@angular/fire/firestore';
import { Select } from '@ngxs/store';
import { AuthUser } from 'app/core/auth/core/auth-user.model';
import { AuthState } from 'app/core/auth/core/auth.state';
import { FirebaseError } from 'app/core/errors/firebase-error';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { CompanySelector } from '../model/company.model';
import { SecurityCompany } from '../model/security-company.model';

@Injectable({
  providedIn: 'root',
})
export class CompanyDataService implements OnDestroy {
  @Select(AuthState.user) user$: Observable<AuthUser>;

  private user: AuthUser;
  private userCompanies = new BehaviorSubject<CompanySelector[]>([]);
  public userCompanies$ = this.userCompanies.asObservable();

  private destroy$ = new Subject<void>();

  constructor(
   private dataServiceFirestore: Firestore) {

    this.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(authUser => {
        this.user = authUser;
        if (this.user) {
          if (this.user.permitNumber) {
            this.getPrincipalRepresentativeCompanies()
              .pipe(takeUntil(this.destroy$))
              .subscribe(companies => this.userCompanies.next(companies));
          } else {
            this.getExternalRepresentativeCompanies()
              .pipe(takeUntil(this.destroy$))
              .subscribe(companies => this.userCompanies.next(companies));
          }
        } else {
          this.userCompanies.next([]);
        }
      });
  }

  get entityUrl(): string {
    return 'societes';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getExternalRepresentativeCompanies() {
    const col = collection(this.dataServiceFirestore, 'security-legal-entity-access');
    const q = query(col, where('externalRepresentativeEmails', 'array-contains', this.user.email));
    return (collectionData(q, { idField: 'id' }) as Observable<SecurityCompany[]>).pipe(
        map(legalEntities =>
           legalEntities.filter(legalEntity => this.shouldFillAnnualDeclaration(legalEntity))),
        map(response => {
          return response.map(data => {
            const result = new CompanySelector();
            result.id = data.id;
            result.legalEntityNumber = data.fileNumber;
            result.legalName = data.legalName;
            return result;
          });
        }),
        catchError(error => {
          const firebaseError = new FirebaseError();
          firebaseError.message = `FirebaseError [getRepresentantCompanies]: Error code => ${error.code} with message ${error.message}`;
          firebaseError.code = error.code;
          console.error(error);
          return of([]);
        }),
        takeUntil(this.destroy$),
      );
  }

  private getPrincipalRepresentativeCompanies() {
    const col = collection(this.dataServiceFirestore, 'security-legal-entity-access');
    const q = query(col, where('principalRepresentative.permitNumber', '==', this.user.permitNumber));
    return (collectionData(q, { idField: 'id' }) as Observable<SecurityCompany[]>).pipe(
        map(legalEntities => legalEntities.filter(legalEntity => this.shouldFillAnnualDeclaration(legalEntity))),
        map(response => {
          return response.map(data => {
            const result = new CompanySelector();
            result.id = data.id;
            result.legalEntityNumber = data.legalEntityNumber;
            result.legalName = data.legalName;
            return result;
          });
        }),
        catchError(error => {
          const firebaseError = new FirebaseError();
          firebaseError.message = `FirebaseError [getRepondantCompanies]: Error code => ${error.code} with message ${error.message}`;
          firebaseError.code = error.code;          
          console.error(error);
          return of([]);
        }),
        takeUntil(this.destroy$),
      );
  }
 

  private shouldFillAnnualDeclaration(legalEntity: SecurityCompany): boolean {

    if (!legalEntity?.practiceStartDate || new Date(legalEntity.enabledPortalRightUntil) < new Date()) return false;
    const startDate = new Date(legalEntity.practiceStartDate);
    const endDate = legalEntity.practiceEndDate ? new Date(legalEntity.practiceEndDate) : null;
    const currentYear = new Date().getFullYear();
    // 1 janvier : date de début avant laquelle les sociétés doivent remplir une da...
    const declarationPeriodStart = new Date(currentYear, 0, 1);
    // ...si leur date de fin n'existe pas, ou bien si date de fin à partir du 1er avril
    const declarationPeriodEnd = new Date(currentYear, 3, 1);

    return startDate < declarationPeriodStart && (!endDate || endDate >= declarationPeriodEnd);
  }

}
